import { useEffect, useMemo, useRef, useState } from 'react';

import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';
import type {
  CategoryEntity,
  CategoryGroupEntity,
  NoteEntity,
} from '@actual-app/core/types/models';
import type { Template } from '@actual-app/core/types/models/templates';

import { separarEmoji } from '#components/mobile/ui/emoji';
import { useCategories } from '#hooks/useCategories';
import { useGlobalPref } from '#hooks/useGlobalPref';
import { useMetadataPref } from '#hooks/useMetadataPref';
import { useQuery } from '#hooks/useQuery';
import { useSpreadsheet } from '#hooks/useSpreadsheet';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { mergeSyncedPrefs, recargarRecuerdos } from '#prefs/prefsSlice';
import { useDispatch, useSelector, useStore } from '#redux';

import { objetivoDesdePlantillas } from './objetivos';
import { parsePagosFijos } from './pagadas';
import {
  adoptar,
  aporteAbierta,
  aporteConFirma,
  aporteConRespuesta,
  baseCerrada,
  baseConCarta,
  baseConCartaHeredada,
  categoriasOcultas,
  cicloDe,
  claseDesdeTravesia,
  claveAporte,
  claveBase,
  claveFoto,
  contarAportaciones,
  esObjetivoConFecha,
  evaluarLlegada,
  mesesDeSerie,
  parseAporte,
  pendienteDeAbrir,
  recuerdosDesdePrefs,
  ridDe,
  serializar,
} from './recuerdos';
import type {
  AporteDispositivo,
  ClaveRecuerdo,
  DatosZarpe,
  ObjetivoConFecha,
  Observacion,
  Recuerdo,
  RecuerdoBase,
  ResultadoLlegada,
  SalidaTravesia,
} from './recuerdos';

/**
 * Hooks de las travesías y sus recuerdos (capa fina sobre `recuerdos.ts`).
 *
 * - Todas las escrituras pasan por `recuerdos/guardar` y
 *   `recuerdos/marcar-llegada` (loot-core), fuera del historial de deshacer.
 * - En un barco de «Solo ver» (`rumboSoloLectura`) no se escribe nada: las
 *   acciones devuelven `false` y el detector no adopta ni marca.
 * - `quien` es el nombre del perfil (`nombrePerfil`).
 */

// ---------------------------------------------------------------------------
// Id de este dispositivo
// ---------------------------------------------------------------------------

const CLAVE_DISPOSITIVO = 'rumbo-recuerdos-dispositivo';
let idEnMemoria: string | null = null;

function idNuevo(): string {
  try {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
  } catch {
    return Math.random().toString(36).slice(2, 14).padEnd(12, '0');
  }
}

/**
 * Id corto y aleatorio de este dispositivo (localStorage; si no hay, uno en
 * memoria para esta sesión). Si se borra, sale otro: la fusión de aportes
 * (mínimo y unión) lo aguanta.
 */
export function idDispositivo(): string {
  if (idEnMemoria) {
    return idEnMemoria;
  }
  let id: string | null = null;
  try {
    id = window.localStorage.getItem(CLAVE_DISPOSITIVO);
  } catch {
    // Sin almacenamiento (modo privado).
  }
  if (!id || !/^[a-z0-9]{6,32}$/.test(id)) {
    id = idNuevo();
    try {
      window.localStorage.setItem(CLAVE_DISPOSITIVO, id);
    } catch {
      // Se queda en memoria.
    }
  }
  idEnMemoria = id;
  return id;
}

// ---------------------------------------------------------------------------
// Lectura
// ---------------------------------------------------------------------------

/** Todos los recuerdos del barco abierto (por prefijo de las prefs). */
export function useRecuerdos(): Recuerdo[] {
  const synced = useSelector(state => state.prefs.synced);
  return useMemo(() => recuerdosDesdePrefs(synced), [synced]);
}

/** Un recuerdo por su rid (`<idCategoría>@<ciclo>`), o null. */
export function useRecuerdo(rid: string | null | undefined): Recuerdo | null {
  const recuerdos = useRecuerdos();
  return useMemo(
    () => (rid ? (recuerdos.find(r => r.base.rid === rid) ?? null) : null),
    [recuerdos, rid],
  );
}

// ---------------------------------------------------------------------------
// Escritura
// ---------------------------------------------------------------------------

function useGuardar() {
  const dispatch = useDispatch();
  const [soloLectura] = useMetadataPref('rumboSoloLectura');
  return async (prefs: Record<ClaveRecuerdo, string>): Promise<boolean> => {
    if (soloLectura || Object.keys(prefs).length === 0) {
      return false;
    }
    try {
      const r = await send('recuerdos/guardar', { prefs });
      if (r?.ok) {
        dispatch(mergeSyncedPrefs(prefs));
        return true;
      }
    } catch {
      // No se pudo guardar: el estado sigue como estaba.
    }
    return false;
  };
}

export type DatosZarpar = Omit<DatosZarpe, 'hoy' | 'autor'> & { hoy?: string };

export type AccionesRecuerdo = {
  /** Este dispositivo puede escribir (no es «Solo ver»). */
  puedeEscribir: boolean;
  /** Id de este dispositivo (para `pendienteDeAbrir`, aportes…). */
  disp: string;
  /** Crea (o completa) la travesía del objetivo con su carta. Devuelve el rid. */
  zarpar: (datos: DatosZarpar) => Promise<string | null>;
  editarCarta: (
    rid: string,
    cambios: Parameters<typeof baseConCarta>[1],
  ) => Promise<boolean>;
  firmarTambien: (rid: string, firma: string) => Promise<boolean>;
  cerrarTravesia: (rid: string, salida: SalidaTravesia) => Promise<boolean>;
  marcarAbierta: (rid: string) => Promise<boolean>;
  responderLlegada: (rid: string, texto: string) => Promise<boolean>;
};

/** Gestos explícitos sobre las travesías. Todos devuelven si se guardó. */
export function useAccionesRecuerdo(): AccionesRecuerdo {
  const store = useStore();
  const [soloLectura] = useMetadataPref('rumboSoloLectura');
  const [nombre] = useGlobalPref('nombrePerfil');
  const guardar = useGuardar();
  const disp = idDispositivo();
  const quien = nombre?.trim() || null;

  const actuales = () => recuerdosDesdePrefs(store.getState().prefs.synced);
  const buscar = (rid: string) =>
    actuales().find(r => r.base.rid === rid) ?? null;
  const miAporte = (rid: string): AporteDispositivo | null =>
    parseAporte(store.getState().prefs.synced[claveAporte(rid, disp)]);
  const guardarAporte = async (
    rid: string,
    nuevo: AporteDispositivo,
    previo: AporteDispositivo | null,
  ) =>
    nuevo === previo
      ? true
      : guardar({ [claveAporte(rid, disp)]: serializar(nuevo) });

  return {
    puedeEscribir: !soloLectura,
    disp,

    zarpar: async datos => {
      const hoy = datos.hoy ?? monthUtils.currentDay();
      const lista = actuales();
      const rid = ridDe(datos.categoriaId, cicloDe(datos.objetivo, hoy));
      const existente = lista.find(r => r.base.rid === rid);
      const prefs: Record<ClaveRecuerdo, string> = {};
      let base: RecuerdoBase;
      if (existente && existente.base.estado === 'navegando') {
        // La adoptó el detector (sin carta): zarpar la completa con el
        // tipo, el nombre y el emoji elegidos ahora.
        base = {
          ...existente.base,
          clase: datos.clase ?? existente.base.clase,
          nombre: datos.nombre || existente.base.nombre,
          emoji: datos.emoji ?? existente.base.emoji,
        };
      } else {
        const adopcion = adoptar({ ...datos, hoy, autor: quien }, lista);
        base = adopcion.base;
        for (const cerrada of adopcion.cerradas) {
          prefs[claveBase(cerrada.rid)] = serializar(cerrada);
        }
      }
      base = baseConCarta(base, {
        ...(datos.carta !== undefined && { carta: datos.carta, autor: quien }),
        ...(datos.siEntonces !== undefined && {
          siEntonces: datos.siEntonces,
        }),
        ...(datos.abrirDesde !== undefined && {
          abrirDesde: datos.abrirDesde,
        }),
      });
      prefs[claveBase(base.rid)] = serializar(base);
      return (await guardar(prefs)) ? base.rid : null;
    },

    editarCarta: async (rid, cambios) => {
      const r = buscar(rid);
      if (!r || r.llegada || r.base.estado !== 'navegando') {
        return false;
      }
      const autor =
        cambios.carta !== undefined && !r.base.autor ? quien : undefined;
      return guardar({
        [claveBase(rid)]: serializar(
          baseConCarta(r.base, {
            ...cambios,
            ...(autor !== undefined && { autor }),
          }),
        ),
      });
    },

    firmarTambien: async (rid, firma) => {
      if (!buscar(rid)) {
        return false;
      }
      const previo = miAporte(rid);
      return guardarAporte(rid, aporteConFirma(previo, firma, quien), previo);
    },

    cerrarTravesia: async (rid, salida) => {
      const r = buscar(rid);
      if (!r || r.llegada || r.base.estado !== 'navegando') {
        return false;
      }
      const hoy = monthUtils.currentDay();
      const prefs: Record<ClaveRecuerdo, string> = {
        [claveBase(rid)]: serializar(baseCerrada(r.base, salida, hoy)),
      };
      if (salida.tipo === 'rumbo') {
        const destino = buscar(salida.destino);
        if (
          !destino ||
          destino.base.rid === rid ||
          destino.base.estado !== 'navegando'
        ) {
          return false;
        }
        prefs[claveBase(destino.base.rid)] = serializar(
          baseConCartaHeredada(destino.base, r.base),
        );
      }
      if (salida.tipo !== 'archivar') {
        // Devolver al mar o cambiar de rumbo: no queda rastro (firmas, foto).
        for (const otro of Object.keys(r.aportes)) {
          prefs[claveAporte(rid, otro)] = '';
        }
        if (r.tieneFoto) {
          prefs[claveFoto(rid)] = '';
        }
      }
      return guardar(prefs);
    },

    marcarAbierta: async rid => {
      const r = buscar(rid);
      if (!r?.llegada) {
        return false;
      }
      const previo = miAporte(rid);
      return guardarAporte(
        rid,
        aporteAbierta(previo, monthUtils.currentDay(), quien),
        previo,
      );
    },

    responderLlegada: async (rid, texto) => {
      const r = buscar(rid);
      if (!r?.llegada) {
        return false;
      }
      const previo = miAporte(rid);
      return guardarAporte(
        rid,
        aporteConRespuesta(previo, texto, quien),
        previo,
      );
    },
  };
}

// ---------------------------------------------------------------------------
// Detector de llegadas
// ---------------------------------------------------------------------------

function plantillasDe(goalDef: string | null | undefined): Template[] {
  if (!goalDef) {
    return [];
  }
  try {
    const v: unknown = JSON.parse(goalDef);
    return Array.isArray(v) ? (v as Template[]) : [];
  } catch {
    return [];
  }
}

async function leerCelda(hoja: string, name: string): Promise<number | null> {
  try {
    const celda = await send('get-cell', { sheetName: hoja, name });
    return typeof celda?.value === 'number' ? celda.value : null;
  } catch {
    return null;
  }
}

/** Serie mensual de la categoría desde el zarpe (meses sin hoja se omiten). */
async function leerSerie(
  categoriaId: string,
  zarpoEn: string,
  hoy: string,
): Promise<Observacion[]> {
  const meses = mesesDeSerie(zarpoEn, hoy);
  const filas = await Promise.all(
    meses.map(async mes => {
      const hoja = monthUtils.sheetForMonth(mes);
      const [saldo, gastado, asignado] = await Promise.all([
        leerCelda(hoja, `leftover-${categoriaId}`),
        leerCelda(hoja, `sum-amount-${categoriaId}`),
        leerCelda(hoja, `budget-${categoriaId}`),
      ]);
      return saldo == null
        ? null
        : { mes, saldo, gastado: gastado ?? 0, asignado: asignado ?? 0 };
    }),
  );
  return filas.filter((f): f is NonNullable<typeof f> => f != null);
}

export type EstadoTravesiaCategoria = {
  categoriaId: string;
  rid: string;
  objetivo: ObjetivoConFecha;
  saldo: number;
  recuerdo: Recuerdo | null;
  resultado: ResultadoLlegada;
};

export type DetectorLlegadas = {
  /** Por categoría con objetivo con fecha (solo las de saldo ya leído). */
  travesias: Map<CategoryEntity['id'], EstadoTravesiaCategoria>;
  /** Llegadas que este dispositivo aún no ha abierto (toca celebrar). */
  pendientes: Recuerdo[];
  /** Categorías ocultas, de grupo oculto o borradas: sin travesía visible. */
  ocultas: ReadonlySet<CategoryEntity['id']>;
};

/**
 * Evalúa las travesías del mes actual: adopta las que no tienen base (sin
 * celebrar las que ya estaban cubiertas) y marca la llegada cuando
 * `saldo ≥ meta` y la travesía está armada. Móntalo una sola vez (p. ej. en
 * Inicio o en el Plan), con `activo` a false donde no haga falta.
 */
const VACIO: { list: CategoryEntity[]; grouped: CategoryGroupEntity[] } = {
  list: [],
  grouped: [],
};

export function useDetectorLlegadas(activo = true): DetectorLlegadas {
  const dispatch = useDispatch();
  const spreadsheet = useSpreadsheet();
  const [soloLectura] = useMetadataPref('rumboSoloLectura');
  const [nombre] = useGlobalPref('nombrePerfil');
  const rawTravesia = useSelector(state => state.prefs.synced.travesia);
  // Un pago fijo con fecha (un seguro anual) no es una travesía.
  const [rawFijos] = useSyncedPref('pagos-fijos');
  const recuerdos = useRecuerdos();
  const guardar = useGuardar();
  const disp = idDispositivo();
  const quien = nombre?.trim() || null;
  const hoy = monthUtils.currentDay();
  const mes = hoy.slice(0, 7);

  const { data: { list: categorias, grouped: grupos } = VACIO } =
    useCategories();
  const ocultas = useMemo(
    () => categoriasOcultas(categorias, grupos),
    [categorias, grupos],
  );
  const { data: notas } = useQuery<NoteEntity>(
    () => q('notes').select('*'),
    [],
  );

  const objetivos = useMemo(() => {
    const notaDe = new Map((notas ?? []).map(n => [n.id, n.note]));
    const fijos = parsePagosFijos(rawFijos);
    const mapa = new Map<
      string,
      { objetivo: ObjetivoConFecha; c: CategoryEntity }
    >();
    for (const c of categorias) {
      if (ocultas.has(c.id) || c.is_income || !c.goal_def || fijos.has(c.id)) {
        continue;
      }
      const objetivo = objetivoDesdePlantillas(
        plantillasDe(c.goal_def),
        notaDe.get(c.id),
      );
      if (esObjetivoConFecha(objetivo) && objetivo.importe > 0) {
        mapa.set(c.id, { objetivo, c });
      }
    }
    return mapa;
  }, [categorias, notas, ocultas, rawFijos]);

  // Celdas vivas del mes actual: null hasta que llegan (nunca se adopta ni
  // se marca con un saldo sin leer).
  const claveIds = [...objetivos.keys()].sort().join('\n');
  const [celdas, setCeldas] = useState<Record<string, number | null>>({});
  useEffect(() => {
    if (!activo || !claveIds) {
      return;
    }
    const hoja = monthUtils.sheetForMonth(mes);
    const unbinds: (() => void)[] = [];
    for (const id of claveIds.split('\n')) {
      for (const campo of ['leftover', 'sum-amount']) {
        const name = `${campo}-${id}`;
        unbinds.push(
          spreadsheet.bind(hoja, { name }, result => {
            const value =
              typeof result.value === 'number' ? result.value : null;
            setCeldas(prev =>
              prev[name] === value ? prev : { ...prev, [name]: value },
            );
          }),
        );
      }
    }
    return () => unbinds.forEach(unbind => unbind());
  }, [activo, claveIds, mes, spreadsheet]);

  const travesias = useMemo(() => {
    const porRid = new Map(recuerdos.map(r => [r.base.rid, r]));
    const mapa = new Map<string, EstadoTravesiaCategoria>();
    for (const [categoriaId, { objetivo }] of objetivos) {
      const saldo = celdas[`leftover-${categoriaId}`];
      if (saldo == null) {
        continue;
      }
      const rid = ridDe(categoriaId, cicloDe(objetivo, hoy));
      const recuerdo = porRid.get(rid) ?? null;
      mapa.set(categoriaId, {
        categoriaId,
        rid,
        objetivo,
        saldo,
        recuerdo,
        resultado: evaluarLlegada({
          objetivo,
          recuerdo,
          saldo,
          serie: [],
          soloLectura: !!soloLectura,
        }),
      });
    }
    return mapa;
  }, [objetivos, celdas, recuerdos, hoy, soloLectura]);

  // Trabajo en curso por rid y series ya miradas (para no releerlas en
  // cada render mientras nada cambia).
  const enCurso = useRef(new Set<string>());
  const mirado = useRef(new Map<string, string>());

  useEffect(() => {
    if (!activo || soloLectura) {
      return;
    }
    for (const t of travesias.values()) {
      const { rid, resultado, recuerdo, objetivo, categoriaId, saldo } = t;
      if (
        resultado.tipo !== 'adoptar' &&
        resultado.tipo !== 'marcar' &&
        resultado.tipo !== 'sin-armar'
      ) {
        continue;
      }
      // Una vez por estado (tipo, saldo, día): sin bucles si algo falla.
      const marca = `${resultado.tipo}|${saldo}|${hoy}`;
      if (enCurso.current.has(rid) || mirado.current.get(rid) === marca) {
        continue;
      }
      mirado.current.set(rid, marca);

      if (resultado.tipo === 'adoptar') {
        const c = objetivos.get(categoriaId)?.c;
        if (!c) {
          continue;
        }
        enCurso.current.add(rid);
        // Nombre y emoji copiados como en «Zarpar» (el emoji va aparte).
        const nombreCopiado = separarEmoji(c.name);
        const { base, cerradas } = adoptar(
          {
            categoriaId,
            objetivo,
            nombre: nombreCopiado.resto || c.name,
            emoji: nombreCopiado.emoji,
            saldo,
            gastado: celdas[`sum-amount-${categoriaId}`] ?? 0,
            hoy,
            clase: claseDesdeTravesia(categoriaId, rawTravesia),
          },
          recuerdos,
        );
        const prefs: Record<ClaveRecuerdo, string> = {
          [claveBase(base.rid)]: serializar(base),
        };
        for (const cerrada of cerradas) {
          prefs[claveBase(cerrada.rid)] = serializar(cerrada);
        }
        void guardar(prefs).finally(() => enCurso.current.delete(rid));
        continue;
      }
      if (!recuerdo) {
        continue;
      }
      enCurso.current.add(rid);
      void (async () => {
        try {
          const serie = await leerSerie(
            categoriaId,
            recuerdo.base.zarpoEn,
            hoy,
          );
          const final = evaluarLlegada({
            objetivo,
            recuerdo,
            saldo,
            serie,
            soloLectura: false,
          });
          if (final.tipo !== 'marcar') {
            return;
          }
          const r = await send('recuerdos/marcar-llegada', {
            rid,
            disp,
            quien,
            llegadaEn: hoy,
            saldo,
            meta: final.meta,
            aportaciones: contarAportaciones(serie, recuerdo.base.zarpoEn),
          });
          if (r?.estado === 'marcada') {
            dispatch(mergeSyncedPrefs({ [r.clave]: r.valor }));
          } else if (
            r?.estado === 'ya-llegada' ||
            r?.estado === 'sin-travesia'
          ) {
            // La pareja se adelantó y aún no nos ha llegado: releer.
            void dispatch(recargarRecuerdos());
          }
        } catch {
          // Se vuelve a intentar cuando cambie el saldo (o mañana).
        } finally {
          enCurso.current.delete(rid);
        }
      })();
    }
  }, [
    activo,
    soloLectura,
    travesias,
    objetivos,
    celdas,
    recuerdos,
    rawTravesia,
    hoy,
    disp,
    quien,
    guardar,
    dispatch,
  ]);

  const pendientes = useMemo(
    () =>
      recuerdos.filter(
        r =>
          r.base.estado === 'navegando' &&
          !ocultas.has(r.base.categoriaId) &&
          pendienteDeAbrir(r, disp),
      ),
    [recuerdos, disp, ocultas],
  );

  return { travesias, pendientes, ocultas };
}
